import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, TrendingUp, Wallet, Users, Scale } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import { fetchSummary, fetchTrend, fetchExpenses } from "../lib/api";
import { eur, formatPeriod, formatDateIt, personColor } from "../lib/format";
import PeriodFilter from "../components/PeriodFilter";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  const diff = (row.Madda || 0) - (row.Doio || 0);
  return (
    <div className="rounded-xl bg-white border border-stone-200 shadow-lg p-3 text-sm" data-testid="trend-tooltip">
      <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>
      <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#D26A50" }} /><span className="text-stone-600">Madda:</span><span className="tabular font-semibold">{eur(row.Madda)}</span></div>
      <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#4A6D5E" }} /><span className="text-stone-600">Doio:</span><span className="tabular font-semibold">{eur(row.Doio)}</span></div>
      <div className="flex items-center gap-2 mt-1 pt-1 border-t border-stone-100"><span className="text-stone-500 text-xs">Differenza:</span><span className="tabular font-semibold" style={{ color: diff >= 0 ? "#D26A50" : "#4A6D5E" }}>{eur(Math.abs(diff))} {diff >= 0 ? "→ Madda" : "→ Doio"}</span></div>
    </div>
  );
};

const PERIOD_LABEL = {
  "01": "Gen", "02": "Feb", "03": "Mar", "04": "Apr", "05": "Mag", "06": "Giu",
  "07": "Lug", "08": "Ago", "09": "Set", "10": "Ott", "11": "Nov", "12": "Dic",
};
const labelFor = (period) => {
  if (!period) return "";
  const [y, m] = period.split("-");
  return `${PERIOD_LABEL[m] || m} '${y.slice(2)}`;
};

export default function DashboardBalance() {
  const [period, setPeriod] = useState({ year: undefined, month: undefined });
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [recent, setRecent] = useState([]);
  const [trendMode, setTrendMode] = useState("12m"); // "12m" | "year"

  useEffect(() => {
    fetchSummary(period).then(setSummary);
    fetchExpenses({ ...period }).then((rows) => setRecent(rows.slice(0, 8)));
  }, [period.year, period.month]);

  useEffect(() => {
    const params = trendMode === "12m" ? { months: 12 } : { year: period.year };
    fetchTrend(params).then(setTrend);
  }, [trendMode, period.year]);

  const trendData = useMemo(
    () => trend.map((t) => ({
      ...t,
      label: trendMode === "12m" ? labelFor(t.period) : formatPeriod(t.period),
      diff: Math.abs((t.Madda || 0) - (t.Doio || 0)),
    })),
    [trend, trendMode]
  );
  const settlement = summary?.settlement;

  return (
    <div className="space-y-8" data-testid="dashboard-balance">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Dashboard 1</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Saldo Madda <span className="text-stone-400">&amp;</span> Doio</h1>
          <p className="text-stone-600 mt-2 max-w-xl">Confronto delle spese sostenute e calcolo automatico di chi deve a chi per riequilibrare le uscite.</p>
        </div>
        <PeriodFilter idPrefix="balance" year={period.year} month={period.month} onChange={setPeriod} />
      </div>

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="card-soft p-8 md:col-span-4" data-testid="card-total">
          <div className="flex items-center gap-3 text-stone-500">
            <Wallet size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Totale spese</span>
          </div>
          <div className="mt-6 font-display text-5xl font-semibold tabular" data-testid="total-amount">{eur(summary?.total)}</div>
          <div className="text-stone-500 text-sm mt-2">{(summary?.madda?.count || 0) + (summary?.doio?.count || 0)} movimenti registrati</div>
        </div>

        <div className="card-soft p-8 md:col-span-4" data-testid="card-madda">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full dot-madda" />
            <span className="text-xs uppercase tracking-widest text-stone-500">Madda ha speso</span>
          </div>
          <div className="mt-6 font-display text-5xl font-semibold tabular" style={{ color: "#D26A50" }} data-testid="madda-amount">{eur(summary?.madda?.total)}</div>
          <div className="text-stone-500 text-sm mt-2">{summary?.madda?.count || 0} movimenti</div>
        </div>

        <div className="card-soft p-8 md:col-span-4" data-testid="card-doio">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full dot-doio" />
            <span className="text-xs uppercase tracking-widest text-stone-500">Doio ha speso</span>
          </div>
          <div className="mt-6 font-display text-5xl font-semibold tabular" style={{ color: "#4A6D5E" }} data-testid="doio-amount">{eur(summary?.doio?.total)}</div>
          <div className="text-stone-500 text-sm mt-2">{summary?.doio?.count || 0} movimenti</div>
        </div>

        <div className="card-soft p-8 md:col-span-7" data-testid="card-settlement">
          <div className="flex items-center gap-3 text-stone-500">
            <Scale size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Pareggio</span>
          </div>
          {settlement?.amount > 0 ? (
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="px-4 py-2 rounded-full text-white font-medium" style={{ background: personColor(settlement.from) }} data-testid="settlement-from">{settlement.from}</span>
                <ArrowRight size={22} strokeWidth={1.5} className="text-stone-400" />
                <span className="px-4 py-2 rounded-full text-white font-medium" style={{ background: personColor(settlement.to) }} data-testid="settlement-to">{settlement.to}</span>
              </div>
              <div className="font-display text-4xl font-semibold tabular ml-auto" data-testid="settlement-amount">{eur(settlement.amount)}</div>
            </div>
          ) : (
            <div className="mt-6 font-display text-3xl font-medium text-stone-600" data-testid="settlement-even">Le spese sono pari, nessun riequilibrio necessario</div>
          )}
          <div className="text-stone-500 text-sm mt-4">
            Quota equa per ciascuno: <span className="tabular font-semibold text-stone-700">{eur(summary?.fair_share)}</span> · Differenza grezza:{" "}
            <span className="tabular font-semibold text-stone-700">{eur(Math.abs(summary?.difference || 0))}</span>
          </div>
        </div>

        <div className="card-soft p-8 md:col-span-5" data-testid="card-split-bar">
          <div className="flex items-center gap-3 text-stone-500">
            <Users size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Ripartizione</span>
          </div>
          <div className="mt-8">
            <div className="h-3 w-full rounded-full bg-stone-100 overflow-hidden flex">
              {summary && (summary.madda.total + summary.doio.total) > 0 ? (
                <>
                  <div style={{ width: `${(summary.madda.total / (summary.madda.total + summary.doio.total)) * 100}%`, background: "#D26A50" }} data-testid="split-madda-bar" />
                  <div style={{ width: `${(summary.doio.total / (summary.madda.total + summary.doio.total)) * 100}%`, background: "#4A6D5E" }} data-testid="split-doio-bar" />
                </>
              ) : null}
            </div>
            <div className="flex justify-between mt-3 text-sm text-stone-600">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full dot-madda" /> Madda</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full dot-doio" /> Doio</span>
            </div>
          </div>
        </div>

        <div className="card-soft p-8 md:col-span-12" data-testid="card-trend">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 text-stone-500">
              <TrendingUp size={18} strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-widest">Andamento mensile</span>
            </div>
            <div className="inline-flex items-center bg-stone-100 rounded-full p-1" data-testid="trend-mode-toggle">
              <button
                onClick={() => setTrendMode("12m")}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${trendMode === "12m" ? "bg-[#377D22] text-white" : "text-stone-600 hover:text-stone-900"}`}
                data-testid="trend-mode-12m"
              >Ultimi 12 mesi</button>
              <button
                onClick={() => setTrendMode("year")}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${trendMode === "year" ? "bg-[#377D22] text-white" : "text-stone-600 hover:text-stone-900"}`}
                data-testid="trend-mode-year"
              >{period.year ? `Anno ${period.year}` : "Tutti gli anni"}</button>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData} margin={{ left: 0, right: 20, top: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="diffFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#A8A29E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#A8A29E" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EBE8E1" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#6E6B67" tick={{ fontSize: 12 }} />
                <YAxis stroke="#6E6B67" tick={{ fontSize: 12 }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="diff" name="Differenza" stroke="none" fill="url(#diffFill)" isAnimationActive={false} />
                <Line type="monotone" dataKey="Madda" stroke="#D26A50" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="Doio" stroke="#4A6D5E" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-soft p-8 md:col-span-12" data-testid="card-recent">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-stone-500">Ultime spese</div>
            <div className="text-xs text-stone-400">{recent.length} mostrate</div>
          </div>
          <div className="divide-y divide-stone-100">
            {recent.length === 0 && (
              <div className="py-8 text-center text-stone-400" data-testid="recent-empty">Nessuna spesa nel periodo selezionato</div>
            )}
            {recent.map((e) => (
              <div key={e.id} className="py-4 flex items-center justify-between gap-4" data-testid={`recent-row-${e.id}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: personColor(e.person) }} />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{e.description || "—"}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      {formatDateIt(e.date)} · {e.category}{e.subcategory ? ` › ${e.subcategory}` : ""}
                    </div>
                  </div>
                </div>
                <div className="tabular font-semibold" style={{ color: personColor(e.person) }}>{eur(e.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
